import { Box, Typography, type Theme } from "@ui";
import UuViewer from "./uu/uuEngine/uuViwer";
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  BudgetService,
  CustomerSharingResponse,
  OnboardingService,
} from "@/shared/api/services";
import type { Debt } from "@/shared/api/types";
import { ThemeColours } from "@/clients/acme/config/colours";
import { useUuFieldController } from "./uu/hooks/useUuFieldController";
import { useAuth } from "@/shared/state/store";
import DocumentPreviewDialog from "./uu/uuUi/DocumentPreviewDialog";
import { SharePageService } from "@/shared/api/services/sharePageService";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";
import { suggestionsHelpContent } from "@/shared/uu/suggestionsHelpContent";
import { FAQ } from "@/engine/renderer/SpecialScreen";
import JsonHelpDialog from "./JsonHelpDialog";
import { ScreenSchema } from "./budget/EditFieldDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import salarySchema from "@/engine/schema/budget/income/1-salary.json";
import benefitsSchema from "@/engine/schema/budget/income/2-benefits.json";
import pensionSchema from "@/engine/schema/budget/income/3-pension.json";
import otherSourcesSchema from "@/engine/schema/budget/income/4-other-sources.json";
import propertiesSchema from "@/engine/schema/budget/asset/1-properties.json";
import otherAssetsSchema from "@/engine/schema/budget/asset/2-other-assets.json";
// Fixed expenses schemas
import householdSchema from "@/engine/schema/budget/fixedExpense/1-household.json";
import utilitiesSchema from "@/engine/schema/budget/fixedExpense/2-utilities.json";
import careSchema from "@/engine/schema/budget/fixedExpense/3-care.json";
import transportSchema from "@/engine/schema/budget/fixedExpense/4-transport.json";
import schoolSchema from "@/engine/schema/budget/fixedExpense/5-school.json";
import pensionInsuranceSchema from "@/engine/schema/budget/fixedExpense/6-pension-insurance.json";
import professionalsSchema from "@/engine/schema/budget/fixedExpense/7-professionals.json";
import otherSourcesFixedSchema from "@/engine/schema/budget/fixedExpense/8-other-sources.json";
// Flexible expense schemas
import leisureSchema from "@/engine/schema/budget/flexibleExpense/1-leisure.json";
import housekeepingSchema from "@/engine/schema/budget/flexibleExpense/2-housekeeping.json";
import personalCostsSchema from "@/engine/schema/budget/flexibleExpense/3-personal-costs.json";
import savingsSchemaFlex from "@/engine/schema/budget/flexibleExpense/4-savings.json";
// Debts schemas
import debtsAdministrationSchema from "@/engine/schema/budget/debts/1-debts-administration.json";
interface Trigger {
  field: string;
  sectionName: string;
  hasHelpDialog: boolean;
  fieldName: string;
  apiFieldName: string;
  headerTitle: string;
  show: boolean;
}

type SmartSuggestionsListUUProps = {
  uuData?: UuData;
  totalDocumentsLength?: number;
  setSuggestionsDocumentsCount?: Dispatch<SetStateAction<number>>;
};

type UuData = {
  potentialChangeInCircumstances?: string;
  hasMedicalConditionAffectingWaterUsage?: string;
};

type ScreenSection = NonNullable<ScreenSchema["sections"]>[number];
type SectionField = NonNullable<ScreenSection["fields"]>[number];

const suggestionHelpKeys = [
  "medicalEvidence",
  "deceasedOccupier",
  "changeInWorkingHours",
  "maritalSplit",
  "redundancy",
  "dropInIncomeDueToIllness",
] as const;

type SuggestionHelpKey = (typeof suggestionHelpKeys)[number];

const isSuggestionHelpKey = (value: string): value is SuggestionHelpKey =>
  suggestionHelpKeys.includes(value as SuggestionHelpKey);

export default function SmartSuggestionsListUU({
  uuData,
  totalDocumentsLength: _totalDocumentsLength = 0,
  setSuggestionsDocumentsCount,
}: SmartSuggestionsListUUProps) {
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [isUuUser, setIsUuUser] = useState<boolean>(false);
  const [schema, setSchema] = useState<any>();
  const [expanded, setExpanded] = useState<boolean>(true);

  const [triggerCountDep, setTriggerCountDep] = useState<boolean>(false);
  const [triggerCount, setTriggerCount] = useState<number>(0);
  const [helpDialogOpen, setHelpDialogOpen] = useState<{
    type: "section" | "field";
    sectionName: string;
    fieldName?: string;
  } | null>(null);

  const [customerShareData, setCustomerShareData] = useState<
    CustomerSharingResponse[]
  >([
    {
      shareAgain: false,
      lastUpdatedBy: 0,
      lastUpdateDate: "",
      subDomain: "",
      isDelete: false,
      hasCreditorPortal: "",
      accountNumber: "",
      creationDate: "",
      schemaName: "",
      styleClass: "",
      isSharing: "",
      isEditable: false,
      createdBy: 0,
      lenderName: "",
      customerId: 0,
      revokeBtn: false,
      creditor: "",
      id: 0,
      shareNow: "",
      lenderOrgId: 0,
    },
  ]);

  const [debts, setDebts] = useState<Debt[]>([]);
  const [loadedDebts, setLoadedDebts] = useState<boolean>(false);
  const [loadedShareData, setLoadedShareData] = useState<boolean>(false);

  const hideUu = UU_SHOW_HIDE_INFO.uuShowHideInfo;

  const { user } = useAuth();
  const uuController = useUuFieldController({ userId: user?.userId ?? null });

  if (hideUu) return null;

  const fetchIncome = async (customerId: number) => {
    try {
      const resp = await BudgetService.getIncomeData(customerId);
      if (resp?.success && Array.isArray(resp.data) && resp.data.length > 0) {
        return resp.data[0] as unknown as Record<string, unknown>;
      }
    } catch {
      // ignore fetch errors; treat as no data
    }
    return null;
  };

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getCustomerShareData() {
      const data: any = await SharePageService.getCustomerSharing(
        customerData.id
      );

      setCustomerShareData(data);
      setLoadedShareData(true);

      return;
    }

    getCustomerShareData();
  }, []);

  useEffect(() => {
    let count = 0;

    for (const t of triggers) {
      const uuFieldState = uuController.getFieldState(t.fieldName);
      if (t.show && uuFieldState?.uploadedDocument) {
        count += 1;
      }
    }

    setTriggerCount(count);
  }, [triggers, triggerCountDep, uuController]);

  useEffect(() => {
    async function getDebts() {
      const resolvedCustomerId: string | number | null = await getCustomerId();
      const debts = await OnboardingService.getDebts(
        Number(resolvedCustomerId),
        {
          skipErrorCapture: true,
        }
      );
      return debts;
    }

    getDebts().then((debts) => {
      setDebts(debts);
      setLoadedDebts(true);
    });
  }, []);

  const isUnitedUtilitiesCustomer = (
    customerData: Record<string, unknown> | null
  ): boolean => {
    if (!customerData) return false;
    const field5TxtFld =
      typeof customerData.field5TxtFld === "string"
        ? customerData.field5TxtFld.trim().toLowerCase()
        : "";

    const hasUUInShare = customerShareData.some((c) => {
      return (
        c.creditor == "United Utilities" &&
        c.isDeleted != "Y" &&
        c.isSharing == "Y"
      );
    });

    const hasUUInDebts = debts.some((c) => {
      return c.creditor == "United Utilities";
    });

    return (
      field5TxtFld.includes("united utilit") || hasUUInDebts || hasUUInShare
    );
  };

  // UU Smart Suggestions
  useEffect(() => {
    if (!loadedDebts || !loadedShareData) {
      return;
    }

    const setupSmartSuggestions = async () => {
      const customerDataStr = localStorage.getItem("customerData");
      if (!customerDataStr) {
        setIsUuUser(false);
        return;
      }

      const customerData = JSON.parse(customerDataStr) as Record<
        string,
        unknown
      >;

      // Only show UU components if customer is referred by United Utilities
      if (!isUnitedUtilitiesCustomer(customerData)) {
        setIsUuUser(false);
        return;
      }

      setIsUuUser(true);

      const incomeData = await fetchIncome(customerData.customerId as number);
      const payload: Record<string, unknown> = { ...incomeData };

      const triggers = [
        {
          fieldName: "universalCredit",
          apiFieldName: "otherIncludeUniversalCredit",
          headerTitle: "Universal Credit Proof",
          show: false,
          hasHelpDialog: true,
          sectionName: "benefits",
          field: "universalCredit",
        },
        {
          fieldName: "pensionCredit",
          apiFieldName: "pensionCredit",
          headerTitle: "Pension Credit Proof",
          show: false,
          hasHelpDialog: true,
          sectionName: "pension",
          field: "pensionCredit",
        },
        {
          fieldName: "housingBenefitLocalAllowance",
          apiFieldName: "housingBenefitLocalAllowance",
          headerTitle: "Local Housing Allowance Proof",
          show: false,
          hasHelpDialog: true,
          sectionName: "benefits",
          field: "housingBenefitLocalAllowance",
        },
        {
          fieldName: "childBenefit",
          apiFieldName: "childBenefit",
          headerTitle: "Child Benefit Proof",
          show: false,
          hasHelpDialog: true,
          sectionName: "benefits",
          field: "childBenefit",
        },
        {
          fieldName: "disabilityAllowance",
          apiFieldName: "dlaPipAttendanceAllowance",
          headerTitle: "Disability Benefit Proof",
          show: false,
          hasHelpDialog: true,
          sectionName: "benefits",
          field: "disabilityAllowance",
        },
      ];

      // set showHide option
      for (const t of triggers) {
        if (parseInt((payload[t.apiFieldName] as string) || "0") > 0) {
          t.show = true;
        }
      }

      const circumstanceSmartSuggestionsMap: Record<string, any> = {
        Bereavement: "deceasedOccupier",
        "Care Leaver aged under 25": null,
        "Drop of working hours": "changeInWorkingHours",
        "Marital Split": "maritalSplit",
        Redundancy: "redundancy",
        "Unable to work due to illness": "dropInIncomeDueToIllness",
        Other: null,
      };

      const potentialChangeKey = uuData?.potentialChangeInCircumstances;
      const mappedFieldName =
        potentialChangeKey &&
        circumstanceSmartSuggestionsMap[potentialChangeKey];

      if (mappedFieldName) {
        const fieldName = mappedFieldName;

        triggers.push({
          fieldName,
          apiFieldName: fieldName,
          headerTitle: "Potential Change in Circumstances Proof",
          show: true,
          hasHelpDialog: true,
          field: fieldName,
          sectionName: "benefits",
        });
      }

      if (uuData?.hasMedicalConditionAffectingWaterUsage == "Y") {
        triggers.push({
          fieldName: "medicalEvidence",
          field: "medicalEvidence",
          apiFieldName: "medicalEvidence",
          headerTitle: "Medical Evidence Proof",
          show: true,
          hasHelpDialog: true,
          sectionName: "benefits",
        });
      }

      setTriggers(triggers);
    };

    setupSmartSuggestions();
  }, [loadedDebts, loadedShareData]);

  const visibleTriggersCount = useMemo(
    () => triggers.filter((t) => t.show).length,
    [triggers]
  );

  function buildDialogSchema(
    groupKey: string,
    fieldKey: string
  ): ScreenSchema | null {
    try {
      const base =
        groupKey === "salary"
          ? salarySchema
          : groupKey === "benefits"
            ? benefitsSchema
            : groupKey === "pension"
              ? pensionSchema
              : groupKey === "otherSources"
                ? otherSourcesSchema
                : groupKey === "properties"
                  ? propertiesSchema
                  : groupKey === "otherAssets"
                    ? otherAssetsSchema
                    : groupKey === "household"
                      ? householdSchema
                      : groupKey === "utilities"
                        ? utilitiesSchema
                        : groupKey === "care"
                          ? careSchema
                          : groupKey === "transport"
                            ? transportSchema
                            : groupKey === "school"
                              ? schoolSchema
                              : groupKey === "pensionInsurance"
                                ? pensionInsuranceSchema
                                : groupKey === "professionals"
                                  ? professionalsSchema
                                  : groupKey === "otherSourcesFixed"
                                    ? otherSourcesFixedSchema
                                    : groupKey === "leisure"
                                      ? leisureSchema
                                      : groupKey === "debtsAdministration"
                                        ? debtsAdministrationSchema
                                        : groupKey === "housekeeping"
                                          ? housekeepingSchema
                                          : groupKey === "personalCosts"
                                            ? personalCostsSchema
                                            : groupKey === "savings"
                                              ? (savingsSchemaFlex as unknown as ScreenSchema)
                                              : salarySchema;

      const cloned = JSON.parse(JSON.stringify(base));
      const section = cloned.sections?.[0];

      if (section && Array.isArray(section.fields)) {
        let found = section.fields.find((f: any) => f.fieldName === fieldKey);
        let savingsWarning: any = null;

        // Search nested children for matches (e.g. gamblingFromHobbiesLeisure)
        if (!found) {
          for (const field of section.fields) {
            if (field.children && Array.isArray(field.children)) {
              const nested = field.children.find(
                (child: any) => child.fieldName === fieldKey
              );
              if (nested) {
                found = nested;
                break;
              }
            }
          }
        }

        // Fallback default
        if (!found) {
          found = section.fields[0];
        }

        // Special logic for savings edit dialog
        if (groupKey === "savings" && fieldKey === "savingsAmount") {
          if (found?.visibleWhen) delete found.visibleWhen;

          // Pull the dynamic warning box
          savingsWarning =
            section.fields.find(
              (f: any) => f.fieldName === "savingsAmountWarningBox"
            ) || null;

          if (savingsWarning) {
            savingsWarning.visibleWhen = {
              op: "gt",
              args: [{ var: "savingsAmount.amount" }, 25],
            };
          }

          section.fields = [found, ...(savingsWarning ? [savingsWarning] : [])];
        } else {
          if (found?.visibleWhen) delete found.visibleWhen;
          section.fields = [found];
        }
      }

      setSchema(cloned);
      return cloned as ScreenSchema;
    } catch {
      return null;
    }
  }

  // Don't render anything if not a UU customer
  if (!isUuUser) {
    return null;
  }

  return (
    <>
      {/* JSON Help Dialog */}
      {helpDialogOpen &&
        (() => {
          console.log(helpDialogOpen);
          const fieldName = helpDialogOpen.fieldName;
          if (fieldName && isSuggestionHelpKey(fieldName)) {
            const helpContent = suggestionsHelpContent[fieldName];

            return (
              <JsonHelpDialog
                open={true}
                onClose={() => setHelpDialogOpen(null)}
                title={helpContent.title || "Help & support"}
                description={helpContent.description}
                faqs={helpContent.faqs || []}
                zIndex={1302}
              />
            );
          }

          const section = schema?.sections?.find((section: ScreenSection) =>
            section.sectionName?.includes(helpDialogOpen.sectionName)
          );

          if (!section) return null;

          let helpConfig:
            | { title?: string; description?: string; faqs: FAQ[] }
            | undefined;

          if (helpDialogOpen.type === "section") {
            helpConfig = section.helpDialog;
          } else if (
            helpDialogOpen.type === "field" &&
            helpDialogOpen.fieldName
          ) {
            const isCalculatedIncomeHelp =
              helpDialogOpen.fieldName.endsWith("_calculatedIncome");
            const actualFieldName = isCalculatedIncomeHelp
              ? helpDialogOpen.fieldName.replace("_calculatedIncome", "")
              : helpDialogOpen.fieldName;

            const field = section.fields?.find(
              (f: SectionField) => f.fieldName === actualFieldName
            );

            if (isCalculatedIncomeHelp) {
              helpConfig = field?.calculatedIncomeHelpDialog;
            } else {
              helpConfig = field?.helpDialog;
            }
          }

          if (!helpConfig) return null;

          console.log(JSON.stringify(helpConfig));

          return (
            <JsonHelpDialog
              open={true}
              onClose={() => setHelpDialogOpen(null)}
              title={helpConfig.title || "Help & support"}
              description={helpConfig.description ?? ""}
              faqs={helpConfig.faqs || []}
              zIndex={1302}
            />
          );
        })()}

      {visibleTriggersCount > 0 && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: "#fff",
            border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Header – clickable accordion toggle */}
          <Box
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 400, fontSize: 20 }}>
              United Utilities Documents
            </Typography>

            {/* Count + Dropdown Icon */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                component="span"
                sx={{
                  backgroundColor: ThemeColours.neon1,
                  paddingX: 1,
                  paddingY: 0.5,
                  borderRadius: 2.5,
                }}
              >
                {`${triggerCount}/${visibleTriggersCount}`}
              </Typography>

              <Box
                sx={{
                  transition: "0.2s ease",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  color: "#444",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 34 }} />
              </Box>
            </Box>
          </Box>

          {/* Collapsible Content Wrapper */}
          <Box
            sx={{
              overflow: "hidden",
              transition: "max-height 0.25s ease",
              maxHeight: expanded ? 4000 : 0,
              mt: expanded ? 2 : 0,
            }}
          >
            {triggers.map((t) => {
              const uuFieldState = uuController.getFieldState(t.fieldName);

              const asyncDateHandler = uuFieldState?.handleDateChange;

              const handleDateChangeWrapper = asyncDateHandler
                ? (month: string, year: string): void => {
                    void asyncDateHandler(month, year);
                  }
                : undefined;

              const handleDocumentUpload = async (file: File) => {
                if (uuFieldState?.handleUploadDocument) {
                  await uuFieldState.handleUploadDocument(file);
                }

                setTriggerCountDep(!triggerCountDep);
                setSuggestionsDocumentsCount?.((prev) => prev + 1);
              };

              const handleDeleteDocument = async () => {
                if (uuFieldState?.handleDeleteDocument) {
                  await uuFieldState.handleDeleteDocument();
                }

                setTriggerCountDep(!triggerCountDep);
                setSuggestionsDocumentsCount?.((prev) => prev - 1);
              };

              return t.show ? (
                <UuViewer
                  key={t.fieldName}
                  {...(t.hasHelpDialog
                    ? {
                        onOpenHelp: () => {
                          buildDialogSchema(t.sectionName, t.field);

                          setHelpDialogOpen({
                            type: "field",
                            sectionName: t.sectionName,
                            fieldName: t.field,
                          });
                        },
                      }
                    : {})}
                  headerTitle={t.headerTitle}
                  fieldName={t.fieldName}
                  {...(handleDateChangeWrapper && {
                    onDateChange: handleDateChangeWrapper,
                  })}
                  initialMonth={uuFieldState?.initialMonth ?? ""}
                  initialYear={uuFieldState?.initialYear ?? ""}
                  dateDisabled={uuFieldState?.isSavingDate ?? false}
                  dateError={Boolean(uuFieldState?.dateError)}
                  dateErrorText={uuFieldState?.dateError ?? ""}
                  showDatePicker={false}
                  showUpload={Boolean(uuFieldState?.handleUploadDocument)}
                  uploadedDocument={uuFieldState?.uploadedDocument ?? null}
                  {...(uuFieldState?.handleUploadDocument && {
                    onDocumentUpload: handleDocumentUpload,
                  })}
                  {...(uuFieldState?.handleDeleteDocument && {
                    onDocumentDelete: handleDeleteDocument,
                  })}
                  {...(uuFieldState?.handleViewDocument && {
                    onDocumentView: uuFieldState.handleViewDocument,
                  })}
                  documentUploading={uuFieldState?.isUploadingDocument ?? false}
                  {...(uuFieldState?.handleDescriptionChange && {
                    onDescriptionChange: uuFieldState.handleDescriptionChange,
                  })}
                />
              ) : null;
            })}
          </Box>
        </Box>
      )}

      <DocumentPreviewDialog
        open={uuController.previewDialog.isOpen}
        onClose={uuController.closePreviewDialog}
        documentName={uuController.previewDialog.documentName}
        documentUrl={uuController.previewDialog.documentUrl}
        isLoading={uuController.previewDialog.isLoading}
        error={uuController.previewDialog.error}
        mimeType={uuController.previewDialog.mimeType}
      />
    </>
  );
}
